import ExcelJS from 'exceljs';

// Convert 24-hour time format to 12-hour format with AM/PM
const convertTo12HourFormat = (time: string): string => {
    if (!time || time === "0" || time === "") {
        return time;
    }
    
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Group data by DIST_NAM, SiteType, and SiteID
const groupByDistrictSiteTypeAndSiteName = (data: any[]) => {
    // First, group all staff by SiteID to ensure all staff for the same site are together
    const sitesBySiteId: { [key: string]: any[] } = {};
    
    data.forEach(item => {
        const siteId = item.SiteID?.toString();
        if (!siteId) return;
        
        if (!sitesBySiteId[siteId]) {
            sitesBySiteId[siteId] = [];
        }
        sitesBySiteId[siteId].push(item);
    });

    // Now process each site group
    return Object.keys(sitesBySiteId).reduce((acc: any, siteId: string) => {
        const siteStaff = sitesBySiteId[siteId];
        if (siteStaff.length === 0) return acc;
        
        // Use the first staff member to get district and site type information
        const firstStaff = siteStaff[0];
        const districtKey = `${firstStaff.DIST_NAM}-${firstStaff.SiteType}`;
        
        if (!acc[districtKey]) {
            acc[districtKey] = {
                DIST_NAM: firstStaff.DIST_NAM,
                SiteType: firstStaff.SiteType,
                sites: {}
            };
        }

        // Use SiteID as the key to ensure consistent grouping regardless of name variations
        if (!acc[districtKey].sites[siteId]) {
            acc[districtKey].sites[siteId] = {
                SiteName: firstStaff.SiteName,
                SiteID: firstStaff.SiteID,
                items: []
            };
        }

        // Process all staff for this site
        siteStaff.forEach(item => {
            acc[districtKey].sites[siteId].items.push(item);
        });

        return acc;
    }, {});
};

// Map site types to their labels
const getSiteTypeLabel = (siteType: number) => {
    switch (siteType) {
        case 1: return "Before School Programs";
        case 2: return "During School Programs";
        case 3: return "After School Programs";
        default: return "";
    }
};

// Generate the Staff Schedule Excel
const StaffSchedule = async (scheduleData: any) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Staff Schedule');

    // Add title
    worksheet.mergeCells('A1:H1');
    worksheet.getCell('A1').value = 'Staff Schedule';
    worksheet.getCell('A1').alignment = { horizontal: 'center' };
    worksheet.getCell('A1').font = { size: 16, bold: true };

    // Group data
    const groupedData = groupByDistrictSiteTypeAndSiteName(scheduleData);

    // Sort district keys alphabetically
    const sortedDistrictKeys = Object.keys(groupedData).sort((a, b) => a.localeCompare(b));
    let currentRow = 3;

    // Iterate over sorted district keys
    sortedDistrictKeys.forEach((districtKey) => {
        const group = groupedData[districtKey];

        // Insert District Name
        worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = group.DIST_NAM;
        worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        currentRow++;

        // Insert Site Type
        worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = getSiteTypeLabel(group.SiteType);
        worksheet.getCell(`A${currentRow}`).font = { bold: true };
        currentRow++;

        // Sort site keys by original SiteName for proper display
        const sortedSiteKeys = Object.keys(group.sites).sort((a, b) => {
            const siteA = group.sites[a];
            const siteB = group.sites[b];
            return (siteA.SiteName || '').localeCompare(siteB.SiteName || '');
        });

        // Iterate over sorted site keys (now SiteID keys)
        sortedSiteKeys.forEach((siteIdKey) => {
            const siteGroup = group.sites[siteIdKey];

            // Insert Site Name
            worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = siteGroup.SiteName;
            worksheet.getCell(`A${currentRow}`).font = { italic: true };
            currentRow++;

            // Add header row for the staff table
            worksheet.getRow(currentRow).values = ['Last Name', 'First Name', 'Role', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
            worksheet.getRow(currentRow).font = { bold: true };
            currentRow++;

            // Insert staff items for the site
            siteGroup.items
            .sort((a: any, b: any) => a.FirstName.localeCompare(b.FirstName)) // Sort by FirstName (since data mapping was swapped)
            .forEach((staff: any) => {
                worksheet.addRow([
                    staff.FirstName,                         // Last Name (corrected)
                    staff.LastName,                          // First Name (corrected)
                    staff.SitePos,                          // Role
                    `${convertTo12HourFormat(staff.MON_B)} - ${convertTo12HourFormat(staff.MON_E)}`,      // Monday
                    `${convertTo12HourFormat(staff.TUE_B)} - ${convertTo12HourFormat(staff.TUE_E)}`,      // Tuesday
                    `${convertTo12HourFormat(staff.WED_B)} - ${convertTo12HourFormat(staff.WED_E)}`,      // Wednesday
                    `${convertTo12HourFormat(staff.THU_B)} - ${convertTo12HourFormat(staff.THU_E)}`,      // Thursday
                    `${convertTo12HourFormat(staff.FRI_B)} - ${convertTo12HourFormat(staff.FRI_E)}`,      // Friday
                ]);
                currentRow++;
            });
        
            // Add a blank row after each site's staff list
            currentRow++;
        });

        // Add a blank row after each district group
        currentRow++;
    });

    // Write to a buffer and return the binary data
    const buffer = await workbook.xlsx.writeBuffer();
    return buffer;
};

export default StaffSchedule;
